'use es6';

import { useState, useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { View } from 'foundations-components/transitional/components/View';
import { LinkIcon } from 'foundations-assets/icons/components/LinkIcon';
import { UnlinkIcon } from 'foundations-assets/icons/components/UnlinkIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MIN_DIMENSION = 1;
const DEFAULT_STEPPER_CONFIG = {
  label: '',
  value: MIN_DIMENSION,
  onChange: () => {}
};
const lockToggleFormattedMessage = {
  enabled: /*#__PURE__*/_jsx(FormattedMessage, {
    message: 'ratioStepper.aspectRatioLocked'
  }),
  disabled: /*#__PURE__*/_jsx(FormattedMessage, {
    message: 'ratioStepper.aspectRatioUnLocked'
  })
};
const getLeftValue = (rightValue, aspectRatio) => Math.round(rightValue * aspectRatio);
const getRightValue = (leftValue, aspectRatio) => Math.round(leftValue * (1 / aspectRatio));
export const Stepper = ({
  name,
  label,
  value,
  isDisabled,
  formatStyle,
  onChange
}) => /*#__PURE__*/_jsx("div", {
  children: /*#__PURE__*/_jsx(FormControl, {
    label: label,
    children: /*#__PURE__*/_jsx(StepperInput, {
      "data-test-id": "radio-stepper",
      formatStyle: formatStyle,
      value: value,
      disabled: isDisabled,
      onChange: ({
        target: {
          value: newValue
        }
      }) => onChange(newValue),
      use: "on-dark",
      "data-editable-field": name
    })
  })
});
export const LockToggle = ({
  isDisabled = false,
  isLocked,
  onClick
}) => {
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(IconButton, {
      disabled: isDisabled,
      use: "transparent",
      placement: "bottom",
      tooltip: lockToggleFormattedMessage[isLocked ? 'enabled' : 'disabled'],
      onClick: () => onClick(!isLocked),
      children: isLocked ? /*#__PURE__*/_jsx(LinkIcon, {}) : /*#__PURE__*/_jsx(UnlinkIcon, {
        color: "fill-tertiary-pressed"
      })
    })
  });
};
export const RatioSteppers = ({
  leftStepper,
  rightStepper,
  isRatioLocked,
  onRatioLockClick,
  isLockDisabled,
  isMaxWidth
}) => /*#__PURE__*/_jsxs(TFlex, {
  "data-test-id": "radio-steppers",
  align: "end",
  justify: "between",
  children: [/*#__PURE__*/_jsx(Stepper, {
    name: "width",
    label: leftStepper.label,
    value: leftStepper.value,
    formatStyle: leftStepper.formatStyle,
    isDisabled: isMaxWidth,
    onChange: leftStepper.onChange
  }), /*#__PURE__*/_jsx(LockToggle, {
    isDisabled: isLockDisabled,
    isLocked: isRatioLocked,
    onClick: onRatioLockClick
  }), /*#__PURE__*/_jsx(Stepper, {
    name: "height",
    label: rightStepper.label,
    value: rightStepper.value,
    formatStyle: leftStepper.formatStyle,
    isDisabled: isMaxWidth,
    onChange: rightStepper.onChange
  })]
});
export const RadioSteppersContainer = ({
  aspectRatio,
  isLocked = true,
  leftStepper = DEFAULT_STEPPER_CONFIG,
  rightStepper = DEFAULT_STEPPER_CONFIG,
  isLockDisabled,
  isMaxWidth,
  toggleMaxWidthLabel,
  onLoad = () => {},
  onLockToggle = () => {},
  onToggleMaxWidth = () => {}
}) => {
  const leftValue = Math.max(MIN_DIMENSION, leftStepper.value);
  const rightValue = Math.max(MIN_DIMENSION, rightStepper.value);
  const [isRatioLocked, setIsRatioLocked] = useState(isLocked);
  const onLeftChange = value => {
    leftStepper.onChange(value);
    if (isRatioLocked) {
      rightStepper.onChange(getRightValue(value, aspectRatio));
    }
  };
  const onRightChange = value => {
    rightStepper.onChange(value);
    if (isRatioLocked) {
      leftStepper.onChange(getLeftValue(value, aspectRatio));
    }
  };
  useEffect(() => {
    onLoad();
  });
  const handleLockClick = () => {
    setIsRatioLocked(!isRatioLocked);
    onLockToggle();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(RatioSteppers, {
      isLockDisabled: isLockDisabled,
      isMaxWidth: isMaxWidth,
      leftStepper: Object.assign({}, leftStepper, {
        value: isMaxWidth ? 100 : leftValue,
        formatStyle: isMaxWidth ? 'percentage' : null,
        onChange: onLeftChange
      }),
      rightStepper: Object.assign({}, rightStepper, {
        value: isMaxWidth ? '' : rightValue,
        onChange: onRightChange
      }),
      isRatioLocked: isRatioLocked,
      onRatioLockClick: handleLockClick
    }), /*#__PURE__*/_jsx(View, {
      as: "div",
      display: "inline-block",
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(Checkbox, {
        checked: isMaxWidth,
        onChange: ({
          target: {
            checked
          }
        }) => onToggleMaxWidth(checked),
        "data-editable-field": "toggle-max-width",
        children: toggleMaxWidthLabel
      })
    })]
  });
};
export default RadioSteppersContainer;